import { useState } from 'react';
import type { JSX } from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { Sharer } from 'src/app/pages/petition/details/shared/types';
import type { PetitionTabProps } from 'src/app/pages/petition/shared/tabTypes';

import { ShareHeadlineProvider, useShareForStartersShareHeadline } from '../hooks/shareHeadline';

import { getSharer } from './api';
import { CommonPetitionDetails } from './CommonPetitionDetails';

type PrefetchedData = Readonly<{
	sharer?: Sharer;
}>;

export const PetitionDetailsShareForStarters = withPrefetchedData(
	({ userInfoState, prefetchedData }: PetitionTabProps & PrefetchedDataProps<PrefetchedData>): JSX.Element => {
		// this allows us to conserve the sharer after first render
		const [sharer] = useState<Sharer | undefined>(prefetchedData?.sharer);
		const { slug, shareChannel } = useKnownAvailableParams<{
			slug: string;
			shareChannel: string;
		}>();

		const { title, description } = useShareForStartersShareHeadline(shareChannel);

		if (!sharer) {
			return <CommonPetitionDetails slug={slug} userInfoState={userInfoState} />;
		}

		return (
			<ShareHeadlineProvider sharer={sharer} title={title} description={description}>
				<CommonPetitionDetails slug={slug} userInfoState={userInfoState} />
			</ShareHeadlineProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [CommonPetitionDetails],
		prefetchData: async (context) => {
			const {
				params: { sharerUserId },
			} = context;
			if (!sharerUserId) return {};
			try {
				return { sharer: await getSharer(sharerUserId, context.utilityContext) };
			} catch (e) {
				return {};
			}
		},
	},
);
